import React, { useState, useMemo } from 'react';
import useNews from '../hooks/useNews';
import usePagination from '../hooks/usePagination';
import HeroNews from '../components/news/HeroNews';
import NewsGrid from '../components/news/NewsGrid';
import NewsPagination from '../components/news/NewsPagination';
import Loader, { SkeletonGrid } from '../components/common/Loader';
import ErrorMessage from '../components/common/ErrorMessage';

const HomePage = ({ searchQuery }) => {
  const { currentPage, goToPage } = usePagination(1);
  const { news, loading, error, refetch } = useNews(null, currentPage);
  
  // Filter news by search query
  const filteredNews = useMemo(() => {
    if (!searchQuery || !searchQuery.trim()) {
      return news;
    }
    const query = searchQuery.toLowerCase();
    return news.filter(item => 
      item.title.toLowerCase().includes(query) ||
      item.source.toLowerCase().includes(query)
    );
  }, [news, searchQuery]);

  // Separate hero news from others
  const heroNews = filteredNews[0];
  const otherNews = filteredNews.slice(1);

  if (loading && currentPage === 1) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SkeletonGrid count={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ErrorMessage message={error} onRetry={refetch} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Page Title */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          {searchQuery ? `"${searchQuery}" için sonuçlar` : 'En Son Haberler'}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {searchQuery 
            ? `${filteredNews.length} haber bulundu`
            : 'Farklı kaynaklardan en güncel haberler'
          }
        </p>
      </div>

      {filteredNews.length === 0 ? (
        <ErrorMessage 
          message={searchQuery 
            ? 'Aramanızla eşleşen haber bulunamadı.' 
            : 'Henüz haber bulunmuyor.'
          } 
        />
      ) : (
        <>
          {/* Hero News - Only show if not searching */}
          {!searchQuery && heroNews && (
            <HeroNews news={heroNews} />
          )}

          {/* News Grid */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
              {searchQuery ? 'Arama Sonuçları' : 'Tüm Haberler'}
            </h2>
            <NewsGrid news={searchQuery ? filteredNews : otherNews} />
          </section>

          {/* Pagination */}
          {!searchQuery && (
            <NewsPagination
              currentPage={currentPage}
              onPageChange={goToPage}
              hasMore={news.length >= 10}
            />
          )}
        </>
      )}

      {loading && currentPage > 1 && (
        <div className="mt-8">
          <Loader text="Daha fazla haber yükleniyor..." />
        </div>
      )}
    </div>
  );
};

export default HomePage;
